import { isClosed, cleanCoordinateDuplicates, simplify, closePath } from '@Utils/Coordinates.js'
import { getElement } from '@Utils/Elements.js'
import { getCenter, getHeight, getWidth } from 'ol/extent.js'
import { LineString } from 'ol/geom'
import Polygon from 'ol/geom/Polygon'
import cspline from 'ol-ext/render/Cspline'
import { ElementFeature } from '@Utils/ElementFeature.js'

export function getElementProperties(feature, properties = {}) {
	let element = getElement(feature.get('elementName'))
	if (!element) {
		return {}
	}
	let elementFeature = new ElementFeature(feature)
	let _opt = elementFeature.getValuesAsObject(true, 'premium')
	for (let v in _opt) {
		let uv = feature.get(v)
		if (uv === undefined || uv === null) {
			properties[v] = element.defaultValues[v]
		} else {
			properties[v] = uv
		}
		if (properties[v] === undefined) {
			properties[v] = _opt[v]
		}
	}
	return properties
}
export function calculateCenter(geometry) {
	let center, coordinates, minRadius
	const type = geometry.getType()
	if (type === 'Polygon') {
		let x = 0
		let y = 0
		let i = 0
		coordinates = geometry.getCoordinates()[0].slice(1)
		coordinates.forEach(function (coordinate) {
			x += coordinate[0]
			y += coordinate[1]
			i++
		})
		center = [x / i, y / i]
	} else if (type === 'LineString') {
		center = geometry.getCoordinateAt(0.5)
		coordinates = geometry.getCoordinates()
	} else {
		center = getCenter(geometry.getExtent())
	}
	let sqDistances
	if (coordinates) {
		sqDistances = coordinates.map(function (coordinate) {
			const dx = coordinate[0] - center[0]
			const dy = coordinate[1] - center[1]
			return dx * dx + dy * dy
		})
		minRadius = Math.sqrt(Math.max.apply(Math, sqDistances)) / 3
	} else {
		minRadius = Math.max(getWidth(geometry.getExtent()), getHeight(geometry.getExtent())) / 3
	}
	return {
		center: center,
		coordinates: coordinates,
		minRadius: minRadius,
		sqDistances: sqDistances
	}
}
export function simplifyFeatureByReducingPoints(feature) {
	let geom = feature.getGeometry()
	let type = geom.getType()
	let coordinates = geom.getCoordinates()

	if (type == 'Polygon') {
		if (coordinates[0].length <= 4) {
			return
		}
		let coords = coordinates.filter((v, i) => i % 2)
		if (!isClosed(coords)) {
			coords.push(coords[0])
		}
		feature.setGeometry(new Polygon([coords]))
	} else if (type == 'LineString') {
		if (coordinates.length <= 3) {
			return
		}
		let coords = coordinates.filter((v, i) => i % 2)
		feature.setGeometry(new LineString(coords))
	}
}
export function simplifyFeature(feature, amount) {
	amount = 1
	let geom = feature.getGeometry()
	let type = geom.getType()
	let coordinates = geom.getCoordinates()

	if (type == 'Polygon') {
		if (coordinates[0].length <= 4) {
			return
		}
		let coords = coordinates[0]
		coords = cleanCoordinateDuplicates(coords, 0)
		coords = simplify(coords, amount, true)
		// let perimeter = getPerimeter(coords);
		// let d = (perimeter / 35) * amount; // 30 for more effect
		// coords = cleanCoordinatesByDistance(d, coords, 0);
		//coords = cleanCoordinates(coordinates[0], 60)
		if (!isClosed(coords)) {
			coords.push(coords[0])
		}
		feature.setGeometry(new Polygon([coords]))
	} else if (type == 'LineString') {
		if (coordinates.length <= 3) {
			return
		}
		let coords = coordinates
		coords = cleanCoordinateDuplicates(coords, 0)
		coords = simplify(coords, amount, true)
		// let perimeter = getPerimeter(coords);
		// let d = (perimeter / 35) * amount; // 30 for more effect
		// coords = cleanCoordinatesByDistance(d, coords, 0);
		//let coords = coordinates.filter((v, i) => i % 2)
		feature.setGeometry(new LineString(coords))
	}
}
export function close(feature) {
	if (feature.getGeometry().getType() === 'LineString') {
		let coords = feature.getGeometry().getCoordinates()
		if (isClosed(coords)) {
			return
		}
		coords.push(coords[0])
		feature.setGeometry(new LineString(coords))
		return
	}
	let coords = feature.getGeometry().getCoordinates()[0]
	if (isClosed(coords)) {
		return
	}
	coords.push(coords[0])
	feature.setGeometry(new Polygon([coords]))
}
export function isFeatureClosed(feature) {
	let geom = feature.getGeometry()
	let type = geom.getType()
	if (type === 'Polygon') {
		return isClosed(geom.getCoordinates()[0])
	} else if (type === 'LineString') {
		return isClosed(geom.getCoordinates())
	}
	return false
}

export function getCSplineGeometry(feature) {
	let el = getElement(feature.get('elementName'))
	let rounded = feature.get('rounded')
	if (rounded === undefined || rounded === null) {
		rounded = el.defaultValues.rounded
	}
	if (!rounded) {
		return feature.getGeometry()
	}
	let g = feature.getGeometry()
	let type = g.getType()
	if (type === 'Polygon') {
		let coordinates = getCSplineCoordinates(feature)
		return new Polygon([coordinates])
	} else if (type === 'LineString') {
		let coordinates = getCSplineCoordinates(feature)
		return new LineString(coordinates)
	} else {
		return feature.getGeometry()
	}
}
export function getCSplineCoordinates(feature, coordinates, resolution = 1, ppsOverride) {
	let geometry = feature.getGeometry()
	let type = geometry.getType()
	if (!coordinates) {
		if (type === 'Polygon') {
			coordinates = geometry.getCoordinates()[0];
		} else if (type === 'LineString') {
			coordinates = geometry.getCoordinates()
		} else {
			return coordinates
		}
	}
	let element = getElement(feature.get('elementName'))
	let defaultValues = {
		tension: 0.5,
		normalize: false,
		pointsPerSeg: 50
	}
	if (element && element.defaultValues) {
		defaultValues = element.defaultValues
	}
	let rounded = feature.get('rounded')
	if (rounded === undefined) {
		rounded = defaultValues.rounded
	}
	if (rounded) {
		let tension = Number(feature.get('tension'))
		let normalize = feature.get('normalize')
		let pointsPerSeg = feature.get('pointsPerSeg')
		if (!tension) {
			tension = defaultValues.tension
		}
		if (!normalize) {
			normalize = defaultValues.normalize
		}
		if (!pointsPerSeg) {
			pointsPerSeg = defaultValues.pointsPerSeg
		}
		if (ppsOverride) {
			pointsPerSeg = ppsOverride
		} else {
			pointsPerSeg = Math.floor(Math.max(pointsPerSeg / resolution, pointsPerSeg))
		}
		try {
			if (type === 'Polygon') {
				let newCoordinates = cspline(coordinates, { tension: tension, pointsPerSeg: pointsPerSeg, normalize: normalize })
				if (newCoordinates.length == 0) {
					console.warn("cspline returned 0 coordinates")
					return coordinates
				}
				return closePath(newCoordinates)
			}
			let lineCoordinates = cspline(coordinates, { tension: tension, pointsPerSeg: pointsPerSeg, normalize: normalize })
			if (lineCoordinates.length == 0) {
				console.warn("cspline returned 0 coordinates")
				return coordinates
			}
		} catch {
			return coordinates
		}

	}
	return coordinates
}
export function doesPolygonsIntersect(feature1, feature2) {
	if (feature1.getGeometry().getType() !== 'Polygon' || feature2.getGeometry().getType() !== 'Polygon') {
		return false
	}
	let coordinatesFeature1 = feature1.getGeometry().getCoordinates()[0]
	let coordinatesFeature2 = feature2.getGeometry().getCoordinates()[0]
	for (let c of coordinatesFeature1) {
		if (feature2.getGeometry().intersectsCoordinate(c)) {
			return true
		}
	}
	for (let c of coordinatesFeature2) {
		if (feature1.getGeometry().intersectsCoordinate(c)) {
			return true
		}
	}
	return false
}
export function isContainedInPolygon(feature1, feature2) {
	let geometry = feature1.getGeometry()
	for (let c of feature2.getGeometry().getCoordinates()[0]) {
		if (!geometry.intersectsCoordinate(c)) {
			return false
		}
	}
	return true
}
export function getIntersectingPolygons(feature, allElements, options = { checkOrder: true, ignoreFocus: true, matchElementName: false, ignoreForests: true }) {
	if (feature.getGeometry().getType() !== 'Polygon') {
		return []
	}
	let arr = []
	for (let f of allElements) {
		if (f === feature) {
			continue
		}
		if (f.get('uid') === feature.get('uid')) {
			continue
		}
		if (f.getGeometry().getType() !== 'Polygon') {
			continue
		}
		if (options.checkOrder && f.get('order') < feature.get('order')) {
			continue
		}
		if (options.ignoreForests && f.get('elementName') === 'forest') {
			continue
		}
		if (options.ignoreFocus && f.get('elementName') === 'focus') {
			continue
		}
		if (options.matchElementName && f.get('elementName') !== feature.get('elementName')) {
			continue
		}
		if (doesPolygonsIntersect(f, feature) === false) {
			continue
		}
		arr.push(f)
	}
	return arr
}